import { useState, useCallback } from 'react';

import { Link } from '../Link';

import { RedoCountdown } from './RedoCountdown';

export const CODE_RESEND_DELAY_MS = 59000;

/**
 * Свойства компонента {@link Redo}
 */
export interface RedoProps {
  /**
   * Текст метки для ссылки или отсчёта времени.
   */
  label: string;

  /**
   * Действие, которое будет выполнено при клике на ссылку.
   */
  action: () => void;

  /**
   * Задержка в миллисекундах, по истечении которой действие можно будет выполнять снова.
   *
   * @defaultValue `CODE_RESEND_DELAY_MS`
   */
  after?: number;

  /**
   * Начальная временная метка для отсчёта времени. Если не указана, используется текущее время.
   */
  timestamp?: number;

  /**
   * Флаг, указывающий, отключён ли компонент.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;
}

/**
 * Компонент Redo, который позволяет пользователю выполнять определённое действие после истечения заданного периода отсчёта.
 * Он переключается между отображением отсчёта времени и кликабельной ссылкой в зависимости от текущего времени и времени окончания отсчёта.
 *
 * @public
 * @param label - Текст метки для ссылки или отсчёта времени.
 * @param action - Действие, которое будет выполнено при клике на ссылку.
 * @param after - Задержка в миллисекундах, по истечении которой действие можно будет выполнять снова.
 * @param timestamp - Начальная временная метка для отсчёта времени. Если не указана, используется текущее время.
 * @param disabled - Флаг, указывающий, отключён ли компонент.
 * @returns Компонент Redo, либо RedoCountdown, либо Link.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link RedoProps}
 */
export const Redo = ({
  label,
  action,
  after = CODE_RESEND_DELAY_MS,
  timestamp,
  disabled = false,
}: RedoProps) => {
  const [endDate, setEndDate] = useState(
    () => (timestamp ?? Date.now()) + after,
  );
  const [isTimerVisible, setIsTimerVisible] = useState(
    () => Date.now() < endDate,
  );

  const toggleTimerVisibility = useCallback(() => {
    setIsTimerVisible((prevShowTimer) => !prevShowTimer);
  }, []);

  const handleSendAgainClick = useCallback(() => {
    setEndDate(Date.now() + after);
    toggleTimerVisibility();
    action();
  }, [after, action, toggleTimerVisibility]);

  return isTimerVisible ? (
    <RedoCountdown
      label={label}
      disabled={disabled}
      finalDate={endDate}
      toggleTimerVisibility={toggleTimerVisibility}
    />
  ) : (
    <Link onClick={handleSendAgainClick} disabled={disabled} stroke={false}>
      {label}
    </Link>
  );
};
